/**
 * Hook générique pour persister un état dans localStorage.
 *
 * Gère : SSR, quota dépassé, mode privé, validation optionnelle.
 * Safe with React 18 Concurrent Mode and multiple hook instances sharing the same key.
 *
 * KEY DESIGN: localStorage writes happen SYNCHRONOUSLY inside the setter,
 * not in a useEffect. This eliminates race conditions when multiple hook
 * instances share the same key (e.g. MissionsConfigurateur + Navigation).
 *
 * @example
 * const [value, setValue] = useLocalStorage<string>('myKey', 'default');
 * const [items, setItems] = useLocalStorage<Item[]>('items', [], isValidItems);
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const LOCAL_STORAGE_SYNC_EVENT = 'app:local-storage-sync';
const SELECTED_MISSIONS_DEBUG_KEY = 'debug:selected-missions';

function isSelectedMissionsStorageKey(key: string): boolean {
  return key === 'selectedMissions' || key.startsWith('selectedMissions_');
}

function isLocalStorageDebugEnabled(key: string): boolean {
  if (!isSelectedMissionsStorageKey(key) || typeof window === 'undefined') return false;

  try {
    const flag = localStorage.getItem(SELECTED_MISSIONS_DEBUG_KEY);
    if (!flag) return false;
    if (flag === 'all') return true;
    return flag === key || flag === key.replace(/^selectedMissions_?/, '');
  } catch {
    return false;
  }
}

function getDebugPayload(raw: string | null | undefined): { rawState: 'null' | 'string'; rawLength?: number; parsedLength?: number } {
  if (raw == null) {
    return { rawState: 'null' };
  }

  const payload: { rawState: 'null' | 'string'; rawLength?: number; parsedLength?: number } = {
    rawState: 'string',
    rawLength: raw.length,
  };

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      payload.parsedLength = parsed.length;
    }
  } catch {
    // Ignore parse errors in debug metadata
  }

  return payload;
}

function debugLocalStorage(key: string, event: string, payload?: Record<string, unknown>, withTrace: boolean = false): void {
  if (!isLocalStorageDebugEnabled(key)) return;

  const href = typeof window !== 'undefined' ? window.location.href : undefined;
  // eslint-disable-next-line no-console
  console.log(`[LSDBG] ${event}`, { key, href, ...(payload || {}) });
  if (withTrace) {
    // eslint-disable-next-line no-console
    console.trace(`[LSDBG TRACE] ${event}`, { key, href, ...(payload || {}) });
  }
}

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  validator?: (parsed: unknown) => T | null
): readonly [T, React.Dispatch<React.SetStateAction<T>>] {
  const readFromStorage = useCallback((storageKey: string): T => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) return defaultValue;
      const parsed = JSON.parse(stored);
      if (validator) {
        const validated = validator(parsed);
        return validated !== null ? validated : defaultValue;
      }
      return parsed as T;
    } catch {
      return defaultValue;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [value, setValue] = useState<T>(() => readFromStorage(key));
  const lastSerializedRef = useRef<string | null>(null);
  // Tracks the latest value for synchronous updater-function resolution.
  const latestValueRef = useRef<T>(value);

  if (lastSerializedRef.current === null && typeof window !== 'undefined') {
    try {
      lastSerializedRef.current = localStorage.getItem(key);
    } catch {
      lastSerializedRef.current = null;
    }
  }

  // Keep latestValueRef in sync with committed React state.
  latestValueRef.current = value;

  // Setter that writes to localStorage SYNCHRONOUSLY, then updates React state.
  const setValueTracked: React.Dispatch<React.SetStateAction<T>> = useCallback(
    (action) => {
      const nextValue = typeof action === 'function'
        ? (action as (prev: T) => T)(latestValueRef.current)
        : action;

      if (typeof window !== 'undefined') {
        try {
          const nextSerialized = JSON.stringify(nextValue);
          if (lastSerializedRef.current !== nextSerialized) {
            localStorage.setItem(key, nextSerialized);
            lastSerializedRef.current = nextSerialized;
            debugLocalStorage(key, 'setValueTracked:write', {
              writerUrl: typeof window !== 'undefined' ? window.location.href : undefined,
              ...getDebugPayload(nextSerialized),
            });
            window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_SYNC_EVENT, {
              detail: {
                key,
                newValue: nextSerialized,
                sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
              },
            }));
          }
        } catch {
          // Silently fail — localStorage peut être plein ou indisponible
        }
      }

      latestValueRef.current = nextValue;
      setValue(nextValue);
    },
    [key]
  );

  const syncFromStorage = useCallback((storageKey: string, nextRaw?: string | null) => {
    if (typeof window === 'undefined') return;
    const raw = typeof nextRaw === 'string' || nextRaw === null ? nextRaw : (() => {
      try {
        return localStorage.getItem(storageKey);
      } catch {
        return null;
      }
    })();

    if (raw === lastSerializedRef.current) {
      debugLocalStorage(storageKey, 'syncFromStorage:skip-same-raw', getDebugPayload(raw));
      return;
    }

    debugLocalStorage(storageKey, 'syncFromStorage:apply', getDebugPayload(raw), true);
    lastSerializedRef.current = raw;
    const nextValue = readFromStorage(storageKey);
    latestValueRef.current = nextValue;
    setValue(nextValue);
  }, [readFromStorage]);

  // Re-lire localStorage quand la clé change (ex: changement de clientId)
  const [prevKey, setPrevKey] = useState(key);
  if (key !== prevKey) {
    setPrevKey(key);
    try {
      lastSerializedRef.current = typeof window === 'undefined' ? null : localStorage.getItem(key);
    } catch {
      lastSerializedRef.current = null;
    }
    debugLocalStorage(key, 'key-change', getDebugPayload(lastSerializedRef.current));
    const nextValue = readFromStorage(key);
    latestValueRef.current = nextValue;
    setValue(nextValue);
  }

  // Cross-tab and cross-instance sync listeners
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      debugLocalStorage(key, 'window-storage-event', {
        sourceUrl: event.url,
        oldValueState: event.oldValue === null ? 'null' : 'string',
        oldValueLength: event.oldValue?.length,
        ...getDebugPayload(event.newValue),
      });
      syncFromStorage(key, event.newValue);
    };

    const onCustomSync = (event: Event) => {
      const customEvent = event as CustomEvent<{ key?: string; newValue?: string | null; sourceUrl?: string }>;
      if (!customEvent?.detail || customEvent.detail.key !== key) return;
      debugLocalStorage(key, 'custom-sync-event', {
        sourceUrl: customEvent.detail.sourceUrl,
        ...getDebugPayload(customEvent.detail.newValue ?? null),
      });
      syncFromStorage(key, customEvent.detail.newValue ?? null);
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(LOCAL_STORAGE_SYNC_EVENT, onCustomSync as EventListener);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(LOCAL_STORAGE_SYNC_EVENT, onCustomSync as EventListener);
    };
  }, [key, syncFromStorage]);

  return [value, setValueTracked] as const;
}

/**
 * Helper bas-niveau pour lire/écrire un flag booléen dans localStorage
 * sans hook React (pour les usages impératifs one-shot).
 */
export function getLocalStorageFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

export function setLocalStorageFlag(key: string): void {
  try {
    localStorage.setItem(key, 'true');
  } catch {
    // Silently fail
  }
}
